import React, { FunctionComponent, useCallback, useEffect, useRef, useState, useMemo } from 'react';

import classNames from 'classnames';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ModuleGroup } from 'admin/groups/Groups';
import { ResponseEditorWidgetPlacement } from 'common/response-editor/ResponseEditor';
import { Overlay } from 'core/overlay/Overlay';
import { usePrevious } from 'core/util/Hook';
import { Feedback } from 'reviews2/feed/feedback-yeti/Feedback';
import { ReviewCardNorthStar } from 'reviews2/feed/review-yeti/card-north-star/ReviewCardNorthStar';
import { ReviewCard } from 'reviews2/feed/review-yeti/card/ReviewCard';
import { ReviewHistoryModal } from 'reviews2/feed/review-yeti/history/review-history/ReviewHistory';
import { ReviewProvider } from 'reviews2/feed/review-yeti/Review.store';
import { ReviewProps } from 'reviews2/feed/review-yeti/Review.types';
import { createContext, stripFunctions } from 'reviews2/feed/review-yeti/Review.util';

import MRActions from './MRActions';

import 'reviews2/feed/review-yeti/ReviewYeti.scss';

const OVERLAY_AREA = 'reviewYeti';

export const ReviewYeti: FunctionComponent<
    ReviewProps & { onError?: (error: string) => void; hideLoadingOverlay?: boolean }
> = ({
    className,
    review,
    reviewId,
    replies,
    config,
    profile,
    handlers,
    options,
    actions,
    mode,
    rid,
    rrid,
    actionMRRenderer,
    reviewTicket,
    onError,
    hideLoadingOverlay,
    isNorthStarEnabled,
}) => {
    const reviewClass = classNames('r4e-review-yeti', className);

    const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
    const [context, setContext] = useState<ReviewProps | null>(null);
    const [hasError, setHasError] = useState<boolean>(false);
    const init = useRef(false);
    const { t } = useTranslation('reviews2');
    const previousOptions = usePrevious(options);
    const previousReviewId = usePrevious(reviewId);
    const previousReplies = usePrevious(replies);
    const [showProvideFeedback, setShowProvideFeedback] = useState(false);

    // --- Ref to hold latest reviewTicket without affecting getContext identity ---
    const reviewTicketRef = useRef(reviewTicket);

    reviewTicketRef.current = reviewTicket;

    // --- Only track the fields that should trigger a full context rebuild ---
    const ticketRebuildKey = useMemo(() => {
        if (!reviewTicket) return null;
        return {
            id: reviewTicket.id,
            agencyID: reviewTicket.agencyID,
            reviewDate: reviewTicket.reviewDate,
            createdDate: reviewTicket.createdDate,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewTicket?.id, reviewTicket?.agencyID, reviewTicket?.reviewDate, reviewTicket?.createdDate]);

    const previousTicketRebuildKey = usePrevious(ticketRebuildKey);

    const getContext = useCallback(async () => {
        const result = await trackPromise(
            createContext(
                DEFAULTS,
                handlers,
                options,
                review,
                reviewId,
                profile,
                config,
                replies,
                actions,
                mode,
                rid,
                rrid,
                t,
                reviewTicketRef.current,
            ),
            OVERLAY_AREA,
        );

        if (result.error) {
            setHasError(true);
            onError?.(result.error);
        } else {
            setContext(result.context);
            setHasError(false);
        }
    }, [handlers, options, review, reviewId, profile, config, replies, actions, mode, rid, rrid, t, onError]);

    // --- Full context rebuild only when critical fields change ---
    useEffect(() => {
        if (
            !init.current ||
            !isEqual(previousReviewId, reviewId) ||
            !isEqual(previousReplies, replies) ||
            !isEqual(previousTicketRebuildKey, ticketRebuildKey) ||
            !isEqual(stripFunctions(options), stripFunctions(previousOptions))
        ) {
            setContext(null);
            setHasError(false);
            getContext();
            init.current = true;
        }
    }, [
        getContext,
        init,
        options,
        previousOptions,
        previousReviewId,
        reviewId,
        previousReplies,
        replies,
        previousTicketRebuildKey,
        ticketRebuildKey,
    ]);

    // --- Lightweight sync: propagate ticket changes to context without rebuilding ---
    useEffect(() => {
        if (context && reviewTicket) {
            setContext((prev) => (prev ? { ...prev, reviewTicket } : prev));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewTicket]);

    // If there's an error, don't render anything (let parent handle it)
    if (hasError) {
        return null;
    }

    if (context?.options?.historyOptions?.showHistory) {
        const handlers = context.handlers || {};

        handlers.reviewHistoryClick = setShowHistoryModal;
    }

    if (context?.review?.taxonomy_categories?.length) {
        const handlers = context.handlers || {};

        handlers.provideFeedback = () => setShowProvideFeedback(true);
    }

    if (!context) {
        return (
            <div className={reviewClass}>
                <Overlay area={OVERLAY_AREA} />
            </div>
        );
    }

    return (
        <div className={reviewClass} id={context.review?.document_id}>
            {!hideLoadingOverlay && <Overlay area={OVERLAY_AREA} />}
            <ReviewProvider review={context.review} isNorthStarEnabled={isNorthStarEnabled}>
                {isNorthStarEnabled ? <ReviewCardNorthStar context={context} /> : <ReviewCard context={context} />}
                {showHistoryModal && <ReviewHistoryModal context={context} show={setShowHistoryModal} />}
                {showProvideFeedback && (
                    <Feedback
                        review={context.review!}
                        show={showProvideFeedback}
                        onClose={() => setShowProvideFeedback(false)}
                    />
                )}
                <MRActions actionMRRenderer={actionMRRenderer} />
            </ReviewProvider>
        </div>
    );
};

const DEFAULTS: Partial<ReviewProps> = {
    options: {
        readOnly: false,
        showEditor: true,
        showSource: true,
        showActions: true,
        showReplies: true,
        showLocation: true,
        showReviewerName: true,
        labelOptions: {
            collapse: false,
        },
        categoryOptions: {
            showLabel: true,
        },
        subRatingOptions: {
            showLabel: true,
        },
        propertyOptions: {
            showLabel: false,
        },
        editorOptions: {
            enableMedia: true,
            enableEmojis: true,
            showCharCount: true,
            moduleGroups: [ModuleGroup.Review],
            widgetPlacement: ResponseEditorWidgetPlacement.Top,
            allowAddCredentials: true,
            filterTemplates: true,
            enableVOBResponding: true,
        },
        repliesOptions: {
            showDelete: true,
            expand: false,
            showToggle: true,
            canAdd: true,
        },
        historyOptions: {
            showHistory: true,
        },
    },
};

ReviewYeti.defaultProps = DEFAULTS;
